import { useEffect, useCallback } from 'react';
import { doc, getDoc, updateDoc, arrayUnion } from 'firebase/firestore';
import { db } from '../firebase/config';

const BADGE_CHECKS = [
  { id: 'streak_7', check: ({ streak }) => streak >= 7 },
  { id: 'streak_30', check: ({ streak }) => streak >= 30 },
  { id: 'topics_50', check: ({ greenCount }) => greenCount >= 50 },
  { id: 'topics_100', check: ({ greenCount }) => greenCount >= 100 },
  { id: 'gap_slayer', check: ({ recentlyGreenedCount }) => recentlyGreenedCount >= 10 },
  { id: 'profile_complete', check: ({ profileCompletion }) => profileCompletion >= 100 },
  { id: 'first_topic', check: ({ greenCount }) => greenCount >= 1 },
  { id: 'first_score', check: ({ scoreCount }) => scoreCount >= 1 },
  { id: 'first_note', check: ({ noteCount }) => noteCount >= 1 },
  { id: 'first_chat', check: ({ chatCount }) => chatCount >= 1 },
  { id: 'first_plan', check: ({ hasPlan }) => !!hasPlan },
  { id: 'first_diagnostic', check: ({ completedDiagnostic }) => !!completedDiagnostic },
  { id: 'first_flashcard', check: ({ flashcardCount }) => flashcardCount >= 1 },
  { id: 'first_lecture', check: ({ completedLecture }) => !!completedLecture },
  { id: 'first_community', check: ({ communityCount }) => communityCount >= 1 },
  { id: 'first_past_paper', check: ({ completedPastPaper }) => !!completedPastPaper },
];

const BADGE_LABELS = {
  streak_7: '🔥 7-day streak unlocked!',
  streak_30: '🔥🔥 30-day streak — incredible!',
  topics_50: '🎓 Scholar — 50 confident topics!',
  topics_100: '🏆 Master — 100 topics confident!',
  gap_slayer: '⚔️ Gap Slayer — 10 topics flipped this month!',
  profile_complete: '✨ Profile complete — Cailyn knows you fully!',
  first_topic: '🗺️ First step — first topic rated confident!',
  first_score: '📊 Scored — first score logged!',
  first_note: '📝 Note-taker — first note created!',
  first_chat: '🤖 Chatty — first AI conversation!',
  first_plan: '📅 Planner — first study plan generated!',
  first_diagnostic: '🩺 Diagnosed — first diagnostic test completed!',
  first_flashcard: '🃏 Flasher — first flashcard deck created!',
  first_lecture: '🎙️ Recorded — first lecture captured!',
  first_community: '🌍 Contributor — first resource shared with the community!',
  first_past_paper: '📄 Paper Pro — first past paper completed!',
};

export default function useBadges({ userId, streak = 0, greenCount = 0, profileCompletion = 0, recentlyGreenedCount = 0, scoreCount = 0, noteCount = 0, chatCount = 0, hasPlan = false, completedDiagnostic = false, flashcardCount = 0, completedLecture = false, communityCount = 0, completedPastPaper = false, showToast } = {}) {
  const checkAndAward = useCallback(async () => {
    if (!userId) return;
    try {
      const profileRef = doc(db, 'userProfile', userId);
      const snap = await getDoc(profileRef);
      if (!snap.exists()) return;
      const existing = snap.data().badges || [];
      const context = { streak, greenCount, profileCompletion, recentlyGreenedCount, scoreCount, noteCount, chatCount, hasPlan, completedDiagnostic, flashcardCount, completedLecture, communityCount, completedPastPaper };
      const toAward = BADGE_CHECKS.filter(b => !existing.includes(b.id) && b.check(context));
      if (!toAward.length) return;
      await updateDoc(profileRef, { badges: arrayUnion(...toAward.map(b => b.id)) });
      if (showToast) {
        toAward.forEach(b => showToast(BADGE_LABELS[b.id] || `Badge earned: ${b.id}`, 'success'));
      }
    } catch (e) {
      
    }
  }, [userId, streak, greenCount, profileCompletion, recentlyGreenedCount, scoreCount, noteCount, chatCount, hasPlan, completedDiagnostic, flashcardCount, completedLecture, communityCount, completedPastPaper, showToast]);

  useEffect(() => {
    checkAndAward();
  }, [checkAndAward]);

  return { checkAndAward };
}
